/**
 * Screen-reader announcements for the pipeline board's drag-and-drop
 * accessibility layer. Extracted from PipelineBoard — see the `announcements`
 * memo there for the call site (`accessibility={{ announcements }}` on
 * `DndContext`).
 */

import type { UniqueIdentifier } from '@dnd-kit/core';

export function buildDragAnnouncements<T>(
  itemByIdMap: Map<string, T>,
  stageLabelMap: Map<string, string>,
  getItemStage: (item: T) => string
) {
  return {
    onDragStart({ active }: { active: { id: UniqueIdentifier } }) {
      const item = itemByIdMap.get(String(active.id));
      const stageLabel = item
        ? stageLabelMap.get(getItemStage(item))
        : stageLabelMap.get(String(active.id));
      return `Picked up item from ${stageLabel || 'column'}. Use arrow keys to move.`;
    },
    onDragOver({
      over,
    }: {
      active: { id: UniqueIdentifier };
      over: { id: UniqueIdentifier } | null;
    }) {
      if (!over) {
        return 'Not over a drop target';
      }
      const overItem = itemByIdMap.get(String(over.id));
      const stageLabel =
        stageLabelMap.get(String(over.id)) ||
        (overItem ? stageLabelMap.get(getItemStage(overItem)) : undefined);
      if (stageLabel) {
        return `Over ${stageLabel} column`;
      }
      return 'Over item';
    },
    onDragEnd({
      over,
    }: {
      active: { id: UniqueIdentifier };
      over: { id: UniqueIdentifier } | null;
    }) {
      if (!over) {
        return 'Dropped outside of a column';
      }
      const overItem = itemByIdMap.get(String(over.id));
      const stageLabel =
        stageLabelMap.get(String(over.id)) ||
        (overItem ? stageLabelMap.get(getItemStage(overItem)) : undefined);
      if (stageLabel) {
        return `Dropped in ${stageLabel} column`;
      }
      return 'Item dropped';
    },
    onDragCancel() {
      return 'Drag cancelled. Item returned to original position.';
    },
  };
}
